import { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Autocomplete, Button, Chip, TextField, Stack, LinearProgress, Tooltip } from '@mui/material';
import * as XLSX from 'xlsx';
import { FileDownloadIcon } from 'components/Icons';
import { useSession } from 'components/Providers';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { searchEnrollmentSummary } from 'store/application/student-enrollment';

const StatusChip = ({ value }) => {
    const n = Number(value)
    if (isNaN(n)) return null
    const color = n === 0 ? 'success' : n > 0 ? 'warning' : 'error'
    const tooltip =
        n === 0
            ? 'On target'
            : n > 0
                ? `Over target — ${n} more students than open spots.`
                : `Under target — ${Math.abs(n)} open spot(s) still to fill.`
    return (
        <Tooltip title={tooltip} placement="right" arrow>
            <Chip label={n} size="small" color={color} sx={{ fontWeight: 600, minWidth: 48 }} />
        </Tooltip>
    )
}

const getOpenSpots = (row, showAnticipated) => (row.targeted ?? 0) - (showAnticipated ? (row.anticipated ?? 0) : (row.enrolled ?? 0))

const getTargetVariance = (row, showAnticipated) => (row.finished ?? 0) + (row.inprogress ?? 0) - getOpenSpots(row, showAnticipated)

const buildColumns = (isTotalsRow, showAnticipated) => [
    {
        field: 'campusname',
        headerName: 'Campus',
        flex: 1,
        renderCell: params => isTotalsRow(params) ? <strong>Total</strong> : params.value,
    },
    {
        field: 'gradelevel',
        headerName: 'Grade Level',
        flex: 1,
        renderCell: params => isTotalsRow(params) ? '' : params.value,
    },
    { field: 'targeted', headerName: 'Targeted Enrollment', flex: 1 },
    showAnticipated
        ? {
            field: 'anticipated',
            headerName: 'Anticipated Enrollment',
            flex: 1,
            renderCell: params => {
                if (isTotalsRow(params)) return params.value
                const retained = params.row.retained
                if (retained != null) {
                    return (
                        <Tooltip title={`${retained} student(s) retained`} placement='right'>
                            <span>{params.value} *</span>
                        </Tooltip>
                    )
                }
                return params.value
            },
        }
        : { field: 'enrolled', headerName: 'Enrolled', flex: 1 },
    {
        field: 'openspots',
        headerName: 'Open Spots',
        flex: 1,
        renderCell: params => getOpenSpots(params.row, showAnticipated),
    },
    { field: 'inprogress', headerName: 'In Progress', flex: 1 },
    { field: 'finished', headerName: 'Finished', flex: 1 },

    {
        field: 'targetAchieved',
        headerName: 'Status',
        flex: 1,
        renderCell: params => <StatusChip value={getTargetVariance(params.row, showAnticipated)} />,
    },
]

const EnrollmentSummary = ({ clientSchools, academicYears = [] }) => {
    const [academicYear, setAcademicYear] = useState(null);
    const { district } = useSession();
    const [selectedSchool, setSelectedSchool] = useState(null);
    const list = useSelector(state => state.application.studentEnrollment.enrollmentSummaryList) || [];
    const loading = useSelector(state => state.application.studentEnrollment.enrollmentSummaryLoading);
    const dispatch = useDispatch();

    const showAnticipated = academicYear?.startDate
        ? dayjs().isBefore(academicYear.startDate)
        : false;

    useEffect(() => {
        if (!academicYear && academicYears.length) {
            setAcademicYear(academicYears.reduce((latest, year) => (dayjs(year.startDate).isAfter(latest.startDate) ? year : latest)));
        }
    }, [academicYears, academicYear]);

    useEffect(() => {
        if (district?.isServiceProvider && !selectedSchool?.id && clientSchools?.length) {
            setSelectedSchool(clientSchools[0]);
        }
    }, [clientSchools, district?.isServiceProvider, selectedSchool?.id]);

    useEffect(() => {
        if (!academicYear?.id) return;
        if (district?.isServiceProvider) {
            if (!selectedSchool?.id) return;
            dispatch(searchEnrollmentSummary({ academicYearProviderId: academicYear.id, schoolId: selectedSchool.id }));
            return;
        }
        dispatch(searchEnrollmentSummary({ academicYearProviderId: academicYear.id }));
    }, [academicYear, selectedSchool?.id, district?.isServiceProvider, dispatch]);

    const totalsRow = list.length > 0 ? {
        id: '__totals__',
        campusname: 'Total',
        gradelevel: '',
        targeted: list.reduce((s, r) => s + (r.targeted ?? 0), 0),
        anticipated: list.reduce((s, r) => s + (r.anticipated ?? 0), 0),
        inprogress: list.reduce((s, r) => s + (r.inprogress ?? 0), 0),
        finished: list.reduce((s, r) => s + (r.finished ?? 0), 0),
        enrolled: list.reduce((s, r) => s + (r.enrolled ?? 0), 0),
    } : null

    const isTotalsRow = params => params.row.id === '__totals__'
    const columns = buildColumns(isTotalsRow, showAnticipated)
    const rows = totalsRow ? [...list, totalsRow] : list

    const exportXLS = () => {
        if (!rows.length) return

        const hasRetained = showAnticipated && list.some(row => row.retained != null)

        const data = rows.map(row => ({
            Campus: row.id === '__totals__' ? 'Total' : (row.campusname ?? ''),
            'Grade Level': row.id === '__totals__' ? '' : (row.gradelevel ?? ''),
            'Targeted Enrollment': row.targeted ?? 0,
            ...(showAnticipated
                ? { 'Anticipated Enrollment': row.anticipated ?? 0 }
                : { Enrolled: row.enrolled ?? 0 }),
            ...(hasRetained ? { Retained: row.retained ?? '' } : {}),
            'Open Spots': getOpenSpots(row, showAnticipated),
            'In Progress': row.inprogress ?? 0,
            Finished: row.finished ?? 0,
            Status: getTargetVariance(row, showAnticipated),
        }))

        const ws = XLSX.utils.json_to_sheet(data)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Enrollment Summary')

        const parts = [academicYear?.label, selectedSchool?.abbreviation, 'Enrollment-Summary',]
        const fileName = `${parts.filter(Boolean).join('-').replace(/[^a-z0-9-]+/gi, '-')}.xlsx`
        XLSX.writeFile(wb, fileName)
    }

    if (district?.isServiceProvider && !clientSchools?.length) return <LinearProgress sx={{ my: 2 }} color="primary" />

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.paper', p: 2, mt: 1, borderRadius: 1 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Autocomplete
                    value={academicYear}
                    onChange={(_, v) => setAcademicYear(v)}
                    options={academicYears}
                    isOptionEqualToValue={(option, val) => option.id === val?.id}
                    size="small"
                    disableClearable
                    sx={{ flex: 1 }}
                    renderInput={params => <TextField {...params} label="Academic Year" />}
                />
                {district?.isServiceProvider && (
                    <Autocomplete
                        options={clientSchools}
                        getOptionLabel={option => option?.abbreviation || ''}
                        value={selectedSchool}
                        onChange={(_, value) => setSelectedSchool(value)}
                        sx={{ flex: 1 }}
                        renderInput={params => (
                            <TextField {...params} label="Select School" variant="outlined" size="small" fullWidth />
                        )}
                        disableClearable
                    />
                )}
                <Button
                    startIcon={<FileDownloadIcon />}
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={exportXLS}
                    disabled={!rows.length}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                    Export Excel
                </Button>
            </Stack>
            <Box sx={{ height: '100%' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={row => row.id || list.indexOf(row) + 1}
                    disableSelectionOnClick
                    hideFooter
                    getRowClassName={params => params.row.id === '__totals__' ? 'totals-row' : ''}
                    loading={loading}
                    sx={{
                        height: 'calc(100vh - 310px)',
                        overflow: 'auto',
                        '& .totals-row': { fontWeight: 700, bgcolor: 'action.hover' },
                        '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' },
                    }}
                    slotProps={{ loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' } }}
                />
            </Box>
        </Box>
    );
};

export default EnrollmentSummary;
